import React from 'react';
import { useIsDisabledByParent } from '../IsDisabledProvider';
import Stack from '../Stack';
import { Color, colors } from '../../../utils/theme';
import { Pressable, View } from 'react-native';
import Icon from '../Icon';
import Text from '../Text';
import { StateStyles, useInteractionStateStyles } from '../../../hooks/useInteractionStateStyles';
import PressableDynamicOpacity from '../PressableDynamicOpacity';

export type CheckboxOwnProps = {
    title?: string;
    value: boolean;
    onChange: (value: boolean) => void;
    isDisabled?: boolean;
    testID?: string;
    indeterminate?: boolean;
    error?: string;
    helpText?: string;
    warnText?: string;
};

export type CheckboxProps = CheckboxOwnProps;

type StateStyle = {
    border: string;
    background: string;
    color: Color;
};

const selectedStateStyle: StateStyle = {
    border: colors['Dark-Text'],
    background: colors['Primary'],
    color: 'White',
};

const stateColors: StateStyles<StateStyle> = {
    default: {
        border: colors['Dark-Text'],
        background: colors['White'],
        color: 'Primary',
    },
    hovered: {
        border: colors['Dark-Text'],
        background: colors['White'],
        color: 'Primary',
    },
    pressed: {
        border: colors['Dark-Text'],
        background: colors['Primary-Light'],
        color: 'Dark-Text',
    },
    inactive: {
        border: colors['Grey'],
        background: colors['Grey-Light'],
        color: 'Grey',
    },
} as const;

export const StandaloneCheckbox: React.FC<Omit<CheckboxProps, 'title' | 'error'> & { hasError: boolean }> = (props) => {
    const isDisabledByParent = useIsDisabledByParent(props.isDisabled);
    const { styles, onPressIn, onPressOut, renderInHoverContainer, state } = useInteractionStateStyles({
        stateStyles: stateColors,
        isDisabled: isDisabledByParent,
    });

    const styleToUse = (props.value || props.indeterminate) && state === 'default' ? selectedStateStyle : styles;

    return renderInHoverContainer(
        <Pressable
            onPressIn={onPressIn}
            onPressOut={onPressOut}
            onPress={() => props.onChange(!props.value)}
            disabled={isDisabledByParent}
            testID={props.testID}
            android_ripple={{ color: 'transparent' }}
        >
            <View
                style={{
                    width: 18,
                    height: 18,
                    borderRadius: 3,
                    borderWidth: 1,
                    borderColor: props.hasError ? colors.Alert : styleToUse.border,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    backgroundColor: styleToUse.background,
                }}
                testID={`checkbox-value-${props.indeterminate ? 'indeterminate' : props.value}`}
            >
                {props.value || props.indeterminate ? (
                    <Icon
                        size="checkbox-icon"
                        name={props.indeterminate ? 'Minus' : 'Check'}
                        color={styleToUse.color}
                    />
                ) : null}
            </View>
        </Pressable>,
    );
};

const Checkbox: React.FC<CheckboxProps> = (props) => {
    return (
        <Stack vertical justify="flex-start" align="flex-start" space={1} fullWidth>
            <Stack horizontal justify="flex-start" align="flex-start" space={2.5} fullWidth>
                <StandaloneCheckbox {...props} hasError={!!props.error} />
                {props.title ? (
                    <PressableDynamicOpacity
                        onPress={() => {
                            props.onChange(!props.value);
                        }}
                        style={{ flex: 1 }}
                        isDisabled={props.isDisabled}
                    >
                        <Text variant="body-1">{props.title}</Text>
                    </PressableDynamicOpacity>
                ) : null}
            </Stack>
            {props.error || props.helpText ? (
                <Text variant={props.error ? 'input-error' : 'input-help-text'}>{props.error || props.helpText}</Text>
            ) : null}
            {props.warnText ? <Text variant="input-warn-text">{props.warnText}</Text> : null}
        </Stack>
    );
};

export default React.memo(Checkbox);
